import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Code2, Plug } from "lucide-react";
import { SectionHead, Eyebrow } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/Reveal";
import { ServiceCard, IndustryCard } from "@/components/site/Cards";
import { CORE_SERVICES } from "@/data/services";
import { INDUSTRY_TYPES } from "@/data/industries";
import { CHALLENGES, FUNCTIONS, SOFTWARE, WHY } from "@/data/home";
import { IMG, altFor } from "@/data/site";

export const Challenges = () => (
  <section className="bg-sand py-20 md:py-32" data-testid="challenges-section">
    <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHead eyebrow="Business challenges" title="Your Business Deserves Better Digital Tools" />
          <Reveal delay={0.12}>
            <p className="mt-6 text-base leading-relaxed text-ink/75">
              Most fitness and wellness businesses grow faster than their systems. Paper registers, spreadsheets, chat threads and unconnected apps create missed renewals, slow follow-ups and double bookings.
            </p>
            <p className="mt-4 text-base leading-relaxed text-ink/75">
              The right digital solutions bring these tasks together: one place for members, bookings and payments, automated reminders that never forget, and a website that keeps working while your team coaches. The result is simpler operations, a smoother customer experience and a stronger base for growth.
            </p>
          </Reveal>
        </div>
      </div>
      <ol className="lg:col-span-7 border-t border-ink/15">
        {CHALLENGES.map((c, i) => (
          <Reveal as="li" key={c} delay={i * 0.03} className="group relative flex items-baseline gap-6 border-b border-ink/15 py-6 md:py-7">
            <span className="font-display text-sm font-bold text-sageDeep">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display text-xl md:text-2xl font-semibold text-ink transition-transform duration-500 group-hover:translate-x-2">{c}</span>
            <span className="absolute bottom-[-1px] left-0 h-[2px] w-0 bg-ink transition-[width] duration-700 group-hover:w-full" aria-hidden />
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const CoreServices = () => (
  <section className="py-20 md:py-32" data-testid="core-services-section">
    <div className="container-x">
      <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <SectionHead eyebrow="Core services" title="Complete Digital Solutions for Fitness & Wellness Businesses" />
        <Reveal><Link to="/services" className="btn-ghost shrink-0" data-testid="all-services-link">All services <ArrowRight className="h-4 w-4" aria-hidden /></Link></Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
        {CORE_SERVICES.map((s, i) => (
          <Reveal key={s.name} delay={(i % 4) * 0.07} className="h-full"><ServiceCard s={s} index={i} /></Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const IndustriesGrid = () => (
  <section className="pb-20 md:pb-32" data-testid="industries-section">
    <div className="container-x">
      <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <SectionHead eyebrow="Industries we serve" title="Technology for Every Fitness, Sports & Wellness Business" />
        <Reveal><Link to="/industries" className="btn-ghost shrink-0" data-testid="all-industries-link">All industries <ArrowRight className="h-4 w-4" aria-hidden /></Link></Reveal>
      </div>
      <div className="mt-14 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6 md:gap-4">
        {INDUSTRY_TYPES.map((it, i) => (
          <Reveal key={it.name} delay={(i % 6) * 0.05}><IndustryCard item={it} index={i} /></Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Functions = () => {
  const [active, setActive] = useState(0);
  const f = FUNCTIONS[active];
  return (
    <section className="border-t border-ink/10 py-20 md:py-32" data-testid="functions-section">
      <div className="container-x">
        <SectionHead eyebrow="Solutions by business function" title="Digital Tools for Every Part of Your Business" />
        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div role="tablist" aria-label="Business functions" className="flex flex-col gap-2 lg:col-span-4">
            {FUNCTIONS.map((fn, i) => {
              const Icon = fn.icon;
              const on = i === active;
              return (
                <button
                  key={fn.id}
                  role="tab"
                  id={`tab-${fn.id}`}
                  aria-selected={on}
                  aria-controls={`panel-${fn.id}`}
                  onClick={() => setActive(i)}
                  className={`group flex min-h-[64px] items-center gap-4 rounded-xl border px-5 py-4 text-left transition-[background-color,border-color,color] duration-300 ${on ? "border-ink bg-ink text-white" : "border-ink/10 bg-white text-ink hover:border-ink/40"}`}
                  data-testid={`function-tab-${fn.id}`}
                >
                  <Icon className={`h-5 w-5 shrink-0 ${on ? "text-lime" : "text-sageDeep"}`} strokeWidth={1.7} aria-hidden />
                  <span className="font-display text-lg font-semibold">{fn.title}</span>
                  <ArrowRight className={`ml-auto h-4 w-4 transition-transform ${on ? "translate-x-0 text-lime" : "-translate-x-1 opacity-0 group-hover:opacity-60"}`} aria-hidden />
                </button>
              );
            })}
          </div>
          <div role="tabpanel" id={`panel-${f.id}`} aria-labelledby={`tab-${f.id}`} className="lg:col-span-8" data-testid="function-panel">
            <motion.div key={f.id} initial={{ opacity: 0.4, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="grid grid-cols-1 gap-8 rounded-2xl border border-ink/10 bg-white p-6 md:grid-cols-2 md:p-8">
              <div className="clip-notch aspect-[4/3] overflow-hidden rounded-lg bg-sand md:aspect-auto md:h-full">
                <img src={IMG(f.image)} alt={altFor(f.image)} loading="lazy" width="1000" height="666" className="h-full w-full object-cover" />
              </div>
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-ink">{f.title}</h3>
                <p className="mt-3 text-base text-ink/75">{f.d}</p>
                <ul className="mt-6 space-y-3">
                  {f.items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-base text-ink"><span className="grid h-6 w-6 place-items-center rounded-full bg-lime"><Check className="h-3.5 w-3.5" aria-hidden /></span>{it}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
            <p className="mt-5 text-sm text-ink/65">Final features and integrations depend on the agreed project scope.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Software = () => (
  <section className="grain relative bg-ink py-20 md:py-32 on-dark" data-testid="software-section">
    <div className="container-x relative">
      <SectionHead dark eyebrow="Specialized software" title="Software That Simplifies Fitness Business Management" intro="Every solution exists for a business reason: fewer manual tasks, fewer missed payments and a better experience for members and staff." />
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Reveal className="rounded-xl border border-lime/40 p-6">
          <Code2 className="h-5 w-5 text-lime" aria-hidden />
          <h3 className="mt-4 text-lg font-semibold text-white">Custom-developed solutions</h3>
          <p className="mt-2 text-sm leading-relaxed text-white/75">Software we design and build around your workflows. You get features shaped to your business instead of adapting to a generic product.</p>
        </Reveal>
        <Reveal delay={0.08} className="rounded-xl border border-sage/60 p-6">
          <Plug className="h-5 w-5 text-[#B5C7AE]" aria-hidden />
          <h3 className="mt-4 text-lg font-semibold text-white">Third-party software integrations</h3>
          <p className="mt-2 text-sm leading-relaxed text-white/75">Where an established tool already fits, such as a payment gateway, calendar, CRM or messaging platform, we integrate and configure it. Each proposal states clearly which parts are custom and which rely on third-party services.</p>
        </Reveal>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {SOFTWARE.map((s, i) => (
          <Link key={s.t} to={`/services/${s.slug}`} className="group flex flex-col bg-ink p-6 transition-colors duration-500 hover:bg-[#2E2F2B]" data-testid={`software-item-${i}`}>
            <span className={`w-fit rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${s.type === "custom" ? "bg-lime text-ink" : "border border-[#B5C7AE] text-[#CFDCC9]"}`}>
              {s.type === "custom" ? "Custom build" : "Custom + integrations"}
            </span>
            <h3 className="mt-5 text-lg font-semibold leading-snug text-white">{s.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/70">{s.d}</p>
            <ArrowUpRight className="mt-auto pt-4 h-8 w-8 text-white/40 transition-colors group-hover:text-lime" aria-hidden />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const Why = () => (
  <section className="py-20 md:py-32" data-testid="why-section">
    <div className="container-x">
      <SectionHead eyebrow="Why Athlevo IT Solution" title="A Digital Partner Focused on Your Growth" />
      <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-[repeat(4,minmax(190px,auto))]">
        {WHY.map((w, i) => {
          const big = i === 0;
          const wide = i === 9;
          return (
            <Reveal
              key={w.t}
              delay={(i % 4) * 0.05}
              className={`${big ? "md:col-span-2 lg:row-span-2" : ""} ${wide ? "md:col-span-2 lg:col-span-4" : ""}`}
            >
              {big ? (
                <div className="relative flex h-full min-h-[360px] flex-col justify-end overflow-hidden rounded-xl bg-ink p-8 on-dark" data-testid="why-card-0">
                  <img src={IMG("studio")} alt={altFor("studio")} loading="lazy" width="1000" height="666" className="absolute inset-0 h-full w-full object-cover opacity-55" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" aria-hidden />
                  <div className="relative">
                    <span className="eyebrow text-lime">01 — Focus</span>
                    <h3 className="mt-4 font-display text-3xl md:text-4xl font-bold text-white">{w.t}</h3>
                    <p className="mt-3 max-w-md text-base text-white/80">{w.d}</p>
                  </div>
                </div>
              ) : (
                <div className={`flex h-full flex-col justify-between rounded-xl border p-6 transition-colors duration-500 hover:border-ink ${wide ? "border-transparent bg-sand" : "border-ink/10 bg-white"}`} data-testid={`why-card-${i}`}>
                  <span className="font-display text-sm font-bold text-sageDeep">{String(i + 1).padStart(2, "0")}</span>
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold leading-snug text-ink">{w.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink/70">{w.d}</p>
                  </div>
                </div>
              )}
            </Reveal>
          );
        })}
      </div>
      <p className="mt-6 text-sm text-ink/65">We do not promise guaranteed revenue growth or search rankings. We commit to clear scope, honest communication and careful delivery.</p>
    </div>
  </section>
);

export const WhoWeHelp = ({ items }) => (
  <section className="py-20 md:py-28" data-testid="who-section">
    <div className="container-x">
      <Eyebrow>Who we help</Eyebrow>
      <ul className="mt-8 flex flex-wrap gap-x-2 gap-y-3 md:gap-y-4">
        {items.map((w, i) => (
          <Reveal as="li" key={w} delay={i * 0.025} className="group flex items-center">
            <span className="font-display text-2xl sm:text-3xl lg:text-[2.6rem] font-semibold leading-tight text-ink transition-colors duration-300 group-hover:text-sageDeep">{w}</span>
            {i < items.length - 1 && <span className="mx-3 md:mx-4 text-2xl lg:text-4xl text-lime [-webkit-text-stroke:1px_#242522]" aria-hidden>/</span>}
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);
